/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { GridOptions } from 'ag-grid-community'
import { Button, Card, Flex, Form, Modal, notification, Typography } from 'antd'
import { getHcErrorData, handleApiError } from '@/utils/api.tsx'
import { HcError, RtkMutation } from '@/common/types/api.def.ts'

type ReportDownloadFormProps<TError = unknown, TErrorItem = unknown> = {
  title: string
  disabled?: boolean
  useMutation: RtkMutation<any, void>
  children: React.ReactNode
  initialValues?: any
  dataTransformer?: (data: any) => any
  columnDefs?: GridOptions<TErrorItem>['columnDefs']
  errorMapper?: (error?: HcError<TError> | null) => TErrorItem[]
}

export const ReportDownloadForm = <TError, TErrorItem, >({
  title,
  disabled = false,
  useMutation,
  columnDefs,
  errorMapper,
  children,
  initialValues,
  dataTransformer,
}: ReportDownloadFormProps<TError, TErrorItem>): JSX.Element => {
  const [trigger, { isLoading }] = useMutation()
  const [errorItems, setErrorItems] = useState<TErrorItem[]>([])
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const gridRef = useRef<AgGridReact<TErrorItem>>(null)

  // eslint-disable-next-line
  const onSubmit = async (data: any) => {
    setErrorItems([])
    setOpenErrorModal(false)

    const result = await trigger(dataTransformer ? dataTransformer(data) : data)
    if ('data' in result) {
      notification.success({ message: 'Download successfully' })
    } else {
      handleApiError(result.error)

      if (!!columnDefs && !!errorMapper) {
        const error = getHcErrorData<TError>(result.error)
        const errorItems = errorMapper(error)

        if (errorItems.length) {
          setErrorItems(errorItems)
          setOpenErrorModal(true)
        }
      }
    }
  }

  return (
    <Card title={title}>
      <Form className="mb-8" layout="vertical" onFinish={onSubmit} disabled={disabled} initialValues={initialValues}>
        {children}
        <Flex justify="flex-end">
          <Button htmlType="submit" type="primary" loading={isLoading} disabled={disabled}>
            Download
          </Button>
        </Flex>
      </Form>
      <Modal open={openErrorModal} onCancel={() => setOpenErrorModal(false)} footer={null} width={1200}>
        <Flex vertical gap="small">
          <Typography.Title level={3}>Detail Errors</Typography.Title>
          <Flex justify="flex-end">
            <Button htmlType="button" onClick={() => gridRef.current?.api.exportDataAsExcel()}>Export</Button>
          </Flex>
          {columnDefs
            ? <div className="ag-theme-quartz w-full h-[600px]">
              <AgGridReact
                ref={gridRef}
                rowData={errorItems}
                columnDefs={columnDefs}
                pivotMode={false}
                pagination
                paginationPageSize={50}
                paginationPageSizeSelector={[50, 100]}
                headerHeight={30}
                rowHeight={25}
              />
            </div>
            : null}
        </Flex>
      </Modal>
    </Card>
  )
}